import { endOfWeek, startOfToday } from "date-fns";
import type { Task } from "@/components/atomic-crm/types";
import { toDateKey } from "@/modules/calendar/calendarUtils";

export type TaskQuickFilter =
  | "open"
  | "in_progress"
  | "due_today"
  | "due_this_week"
  | "overdue"
  | "completed";

export const TASK_QUICK_FILTERS: { value: TaskQuickFilter; label: string }[] = [
  { value: "open", label: "Open tasks" },
  { value: "in_progress", label: "Tasks in progress" },
  { value: "due_today", label: "Tasks due today" },
  { value: "due_this_week", label: "Tasks due this week" },
  { value: "overdue", label: "Overdue tasks" },
  { value: "completed", label: "Completed tasks" },
];

export const getTaskQuickFilterLabel = (filter: TaskQuickFilter) =>
  TASK_QUICK_FILTERS.find((entry) => entry.value === filter)?.label ??
  "Open tasks";

const matchesQuickFilter = (
  task: Task,
  filter: TaskQuickFilter,
  now: Date,
): boolean => {
  if (filter === "completed") return Boolean(task.done_date);
  if (task.done_date) return false;
  if (filter === "open") return true;

  const due = new Date(task.due_date).getTime();
  if (Number.isNaN(due)) return false;
  const todayStart = startOfToday().getTime();
  const tomorrowStart = todayStart + 24 * 60 * 60 * 1000;

  if (filter === "overdue") return due < todayStart;
  if (filter === "in_progress") {
    // Multi-day task that has started and is not overdue yet.
    const start = task.start_date?.slice(0, 10);
    return Boolean(start) && start! <= toDateKey(now) && due >= todayStart;
  }
  if (filter === "due_today") return due >= todayStart && due < tomorrowStart;
  return (
    due >= todayStart && due <= endOfWeek(now, { weekStartsOn: 0 }).getTime()
  );
};

export const filterTasksByQuickFilter = (
  tasks: Task[],
  filter: TaskQuickFilter,
  now = new Date(),
) => tasks.filter((task) => matchesQuickFilter(task, filter, now));

export const countTasksByQuickFilter = (
  tasks: Task[],
  now = new Date(),
): Record<TaskQuickFilter, number> => {
  const counts = {} as Record<TaskQuickFilter, number>;
  for (const { value } of TASK_QUICK_FILTERS) {
    counts[value] = filterTasksByQuickFilter(tasks, value, now).length;
  }
  return counts;
};
